import { checkSettingValue, formatMoneyAmount, formatPercentValue, isMoneyValue, type SettingInput } from '@till-erp/settings/values';
import type { SettingView } from './api.ts';
import { isDirty } from './form-state.ts';
import { blurError } from './field-check.ts';

/**
 * Einstellungs-Karte (Story 1.27, DESIGN „Einstellungs-Karte“, Change 04.10.2026 Formulare kompakt §4.3): reine Logik
 * ohne React – Karten einer Gruppe bilden, Formularwerte ableiten, Änderungen für `POST /settings/batch` sammeln, Fehler
 * der Schnittstelle den Feldern zuordnen. Geprüft wird mit demselben Zod-Schema wie im Command (`checkSettingValue`);
 * ein Datum („Gültig ab“) gibt es nicht – Einstellungen gelten ab dem Speichern (Rudolf F3).
 */

export type TableDraft = Record<string, string>[];
export type Draft = string | boolean | TableDraft;

/** Eine Karte: ein Abschnitt (`section`) der Gruppe oder – ohne Abschnitte – die ganze Gruppe. */
export interface SettingCardModel {
  /** Kennung für React und Tests: `<gruppe>` bzw. `<gruppe>:<abschnitt>`. */
  id: string;
  title: string;
  /** Beschreibung der Gruppe unter dem Kopf (nur bei der Karte ohne Abschnitt). */
  description: string | null;
  settings: SettingView[];
}

/**
 * Karten einer Gruppe: je Abschnitt eine Karte in der Reihenfolge, in der der Abschnitt zuerst vorkommt; Einstellungen
 * ohne Abschnitt bilden eine Karte mit dem Namen der Gruppe (vorneweg).
 */
export function cardsOf(group: { id: string; label: string; description: string }, settings: readonly SettingView[]): SettingCardModel[] {
  const cards = new Map<string | null, SettingCardModel>();
  for (const s of settings) {
    const section = s.section;
    let card = cards.get(section);
    if (!card) {
      card =
        section === null
          ? { id: group.id, title: group.label, description: group.description, settings: [] }
          : { id: `${group.id}:${section}`, title: section, description: null, settings: [] };
      cards.set(section, card);
    }
    card.settings.push(s);
  }
  const plain = cards.get(null);
  return plain ? [plain, ...[...cards.values()].filter((c) => c !== plain)] : [...cards.values()];
}

/**
 * Zeilen einer Karte (Rudolf 06.10.2026): aufeinanderfolgende Einstellungen mit gleicher `row` bilden eine Zeile (z. B.
 * PLZ, Ort, Land). Ohne `row` (`null`) bleibt es wie bisher: alle aufeinanderfolgenden Einstellungen ohne Zeile in einer
 * fließenden Formular-Zeile.
 */
export function rowsOf<T extends { row: string | null }>(settings: readonly T[]): T[][] {
  const rows: T[][] = [];
  let last: string | null | undefined;
  for (const s of settings) {
    const current = rows.at(-1);
    if (current && s.row === last) current.push(s);
    else rows.push([s]);
    last = s.row;
  }
  return rows;
}

/**
 * Einstellungen einer Karte in normale und selten nötige (`advanced`, Bereich „Erweitert“, Ticket 1.209) teilen;
 * Reihenfolge bleibt in beiden Teilen.
 */
export function splitAdvanced<T extends { key: string }>(settings: readonly T[], advancedKeys: ReadonlySet<string>): { normal: T[]; advanced: T[] } {
  return { normal: settings.filter((s) => !advancedKeys.has(s.key)), advanced: settings.filter((s) => advancedKeys.has(s.key)) };
}

/** Wert, den das Feld zeigt: was jetzt gilt (Altbestand geplanter Versionen steht als Zeile darunter). */
export function shownValue(setting: SettingView): unknown {
  return setting.effective ? setting.effective.value : setting.value;
}

/** Datum einer Einstellung mit `format: 'date'` (Story 4.23): gespeichert ISO „2027-02-01“, gezeigt „01.02.2027“. */
export function settingDateText(value: unknown): string | null {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}` : null;
}

/** Gespeicherter Wert → Formularwert (Text-Felder, Cent als „4,90“, Datum als „01.02.2027“). */
export function toDraft(input: SettingInput, value: unknown): Draft {
  switch (input.kind) {
    case 'boolean':
      // Fehlender Wert gilt wie auf dem Server als „an“ (sicherer Zustand beim zweiten Faktor).
      return value !== false;
    case 'table':
      return (Array.isArray(value) ? (value as Record<string, unknown>[]) : []).map((row) =>
        Object.fromEntries(
          input.columns.map((c) => {
            const cell = row[c.key];
            if (c.type === 'money' && isMoneyValue(cell)) return [c.key, formatMoneyAmount(cell.amountMinor)];
            return [c.key, cell === null || cell === undefined ? '' : String(cell)];
          }),
        ),
      );
    case 'money':
      return isMoneyValue(value) ? formatMoneyAmount(value.amountMinor) : value === null || value === undefined ? '' : String(value);
    case 'percent':
      return formatPercentValue(value);
    default:
      if (input.kind === 'text' && input.format === 'date') return settingDateText(value) ?? (value === null || value === undefined ? '' : String(value));
      return value === null || value === undefined ? '' : String(value);
  }
}

/** Formularwerte aller änderbaren Einstellungen einer Karte, wie geladen. */
export function initialDrafts(settings: readonly SettingView[]): Record<string, Draft> {
  return Object.fromEntries(settings.filter((s) => s.editable).map((s) => [s.key, toDraft(s.input, shownValue(s))]));
}

/** Grundlage der Eingaben: Formularwerte und Versionen je Schlüssel (AD-4: gespeichert wird gegen die gelesene Version). */
export interface DraftBase {
  initial: Record<string, Draft>;
  versions: Record<string, number>;
}

/** Grundlage aus dem Stand des Servers (nur änderbare Einstellungen). */
export function baseOf(settings: readonly SettingView[]): DraftBase {
  const editable = settings.filter((s) => s.editable);
  return { initial: initialDrafts(editable), versions: Object.fromEntries(editable.map((s) => [s.key, s.version])) };
}

/** Festgehaltene Grundlage eines Schlüssels (abweichend vom Stand des Servers). */
export type Pins = Record<string, { version: number; initial: Draft }>;

/** Grundlage mit festgehaltenen Schlüsseln. */
export function withPins(server: DraftBase, pins: Pins): DraftBase {
  return {
    initial: { ...server.initial, ...Object.fromEntries(Object.entries(pins).map(([k, p]) => [k, p.initial])) },
    versions: { ...server.versions, ...Object.fromEntries(Object.entries(pins).map(([k, p]) => [k, p.version])) },
  };
}

/**
 * Neuer Stand vom Server (eigenes Speichern, Nachladen, Zustellung): unveränderte Felder übernehmen ihn. Ein Feld mit
 * eigener Eingabe behält Eingabe **und** die Version, von der die Eingabe ausging – so meldet das Speichern eine
 * inzwischen gespeicherte fremde Änderung (409, „Neu von …“) statt sie still zu überschreiben (Prüfliste 8, EXPERIENCE
 * §4.3). Festgehaltene Schlüssel, deren Version der Server inzwischen hat, werden wieder frei.
 */
export function rebase(prev: DraftBase, next: DraftBase, pins: Pins, drafts: Record<string, Draft>): { pins: Pins; drafts: Record<string, Draft> } {
  const before = withPins(prev, pins);
  const nextPins: Pins = {};
  const nextDrafts: Record<string, Draft> = {};
  for (const key of Object.keys(next.initial)) {
    const pin = pins[key];
    const edited = key in drafts && key in before.initial && isDirty(before.initial[key], drafts[key]);
    // Ein Pin gilt nur, solange das Feld eine eigene Eingabe hat (zurückgetippt = wieder der Stand des Servers).
    if (pin && edited && pin.version !== next.versions[key]) nextPins[key] = pin;
    else if (!pin && edited && prev.versions[key] !== next.versions[key]) nextPins[key] = { version: prev.versions[key]!, initial: prev.initial[key]! };
    nextDrafts[key] = edited ? drafts[key]! : next.initial[key]!;
  }
  return { pins: nextPins, drafts: nextDrafts };
}

/**
 * „Verwerfen“: alle Felder auf den Stand des Servers, festgehaltene Versionen fallen weg – sonst bliebe nach einer
 * fremden Änderung die alte Grundlage stehen und die Karte gälte beim nächsten Nachladen wieder als geändert.
 */
export function discardAll(server: DraftBase): { pins: Pins; drafts: Record<string, Draft> } {
  return { pins: {}, drafts: { ...server.initial } };
}

/** Schlüssel, deren Formularwert vom geladenen abweicht (Rückkehr zum alten Wert = unverändert). */
export function changedKeys(initial: Record<string, Draft>, drafts: Record<string, Draft>): string[] {
  return Object.keys(drafts).filter((key) => isDirty(initial[key], drafts[key]));
}

/** Eine Änderung für `POST /settings/batch`: Schlüssel, Rohwert (Server prüft und wandelt), gelesene Version (AD-4). */
export interface SettingChange {
  key: string;
  value: Draft;
  version: number;
}

/**
 * Vorprüfung aller geänderten Felder mit dem Schema des Commands und die Änderungen fürs Speichern. Fehler stehen unter
 * dem Schlüssel der Einstellung (`<key>`, Tabellenzellen `<key>.<zeile>.<spalte>`) – wie die Antwort der Schnittstelle.
 */
export function prepareSave(
  settings: readonly SettingView[],
  base: DraftBase,
  drafts: Record<string, Draft>,
): { ok: true; changes: SettingChange[] } | { ok: false; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const changes: SettingChange[] = [];
  for (const key of changedKeys(base.initial, drafts)) {
    const setting = settings.find((s) => s.key === key);
    if (!setting) continue;
    const draft = drafts[key]!;
    const checked = checkSettingValue(setting.input, draft);
    if (!checked.ok) {
      for (const [field, message] of Object.entries(checked.errors)) errors[`${key}${field.slice('value'.length)}`] = message;
      continue;
    }
    changes.push({ key, value: draft, version: base.versions[key] ?? setting.version });
  }
  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, changes };
}

/**
 * Feld verlassen (Ticket 1.256): Satz dieser Einstellung aus derselben Prüfung wie Speichern (`prepareSave` →
 * `checkSettingValue`); unveränderter Wert ohne Meldung, alte Meldung des Felds verschwindet.
 */
export function settingBlurErrors(
  errors: Record<string, string>,
  key: string,
  settings: readonly SettingView[],
  base: DraftBase,
  drafts: Record<string, Draft>,
): Record<string, string> {
  const result = prepareSave(settings, base, drafts);
  return blurError(errors, key, { changed: changedKeys(base.initial, drafts).includes(key), saveErrors: result.ok ? {} : result.errors });
}

/**
 * Fehler der Schnittstelle (`errors` je `<key>` bzw. `<key>.<zeile>.<spalte>`) auf die Felder der Karte verteilen; Sätze
 * ohne passendes Feld (z. B. `changes`, `validFrom`) kommen als Hinweis in die Karte – kein Satz geht verloren.
 */
export function splitErrors(keys: readonly string[], errors: Record<string, string>): { fields: Record<string, string>; rest: string[] } {
  const fields: Record<string, string> = {};
  const rest: string[] = [];
  for (const [field, message] of Object.entries(errors)) {
    const key = keys.find((k) => field === k || field.startsWith(`${k}.`));
    if (key) fields[field] ??= message;
    else if (!rest.includes(message)) rest.push(message);
  }
  return { fields, rest };
}

/**
 * Höchstlänge eines Labels, das samt „?“ (`HelpHint`) über einem schmalen Feld in dessen eigener Spalte Platz hat
 * (Spalte `xs` 96 px, `s` 144 px; Schrift `type-meta` etwa 7 px je Zeichen, „?“ etwa 26 px).
 */
const SHORT_LABEL = { xs: 10, s: 16 } as const;
/** Längstes Label, das samt „?“ in eine mittlere Spalte (`field-m`) passt; darüber breite Spalte (Ticket 1.268). */
const MID_LABEL = 28;

/**
 * Spaltenbreite einer Feldzeile (Label · „?“ · Feld): Die Beschreibung steht hinter dem „?“ (Ticket 1.157) und zählt
 * nicht mehr mit; ein langes Label braucht aber Platz, darum stehen kurze Felder (`xs`, `s`) dann in einer mittleren
 * Spalte. Passt das Label über das Feld (z. B. „PLZ“), bleibt die Spalte so schmal wie das Feld und kurze Felder passen
 * nebeneinander (PLZ neben Ort, Rudolf 05.10.2026). Ist das Label auch für die mittlere Spalte zu lang (z. B. „Höchstalter
 * der Datenbank-Sicherung“), steht es in einer breiten Spalte, damit Label und „?“ in einer Zeile bleiben (Ticket 1.268).
 */
export function settingColumnWidth(setting: { input: SettingInput; label: string }): 'xs' | 's' | 'm' | 'l' | 'full' {
  const width = settingFieldWidth(setting.input);
  if (width !== 'xs' && width !== 's') return width;
  if (setting.label.length <= SHORT_LABEL[width]) return width;
  return setting.label.length <= MID_LABEL ? 'm' : 'l';
}

/** Feldbreite nach Inhalt (DESIGN `spacing.field-*`): Zahl schmal, E-Mail lang, Text nach Format bzw. Höchstlänge. */
export function settingFieldWidth(input: SettingInput): 'xs' | 's' | 'm' | 'l' | 'full' {
  switch (input.kind) {
    case 'integer':
    case 'money':
    case 'percent':
      return 's';
    case 'user':
      // Personen-Auswahl (Story 12.11): Namen wie „Buchhaltung (Test)“.
      return 'm';
    case 'currency':
      // Auswahl mit Suche „CHF – Schweizer Franken“ (Ticket 1.262), nicht mehr das 3-Zeichen-Feld.
      return 'm';
    case 'email':
      return 'l';
    case 'text': {
      if (input.format === 'bic' || input.format === 'date') return 's';
      if (input.format === 'phone') return 'm';
      if (input.format === 'email' || input.format === 'url' || input.format === 'iban') return 'l';
      const max = input.maxLength ?? 500;
      if (max <= 12) return 's';
      if (max <= 40) return 'm';
      if (max <= 120) return 'l';
      return 'full';
    }
    case 'select': {
      // Breite nach der längsten Auswahl, damit kein Wert abgeschnitten wird.
      const longest = Math.max(0, ...input.options.map((o) => o.label.length));
      if (longest <= 20) return 'm';
      if (longest <= 35) return 'l';
      return 'full';
    }
    case 'boolean':
    case 'table':
      return 'full';
    default:
      return 'm';
  }
}

// Such-Faltung und Treffer-Prüfung liegen neutral in `search.ts` (auch für Seiten ohne Einstellungen).
export { matchesSearch, searchText } from './search.ts';
